import { FC, ReactNode, createContext, useState } from 'react'
import { useUA } from 'context'
import { useRouter } from 'next/router'
import { useCurrentUserContext } from 'hooks/useCurrentUser'
import { LINKAREER_BASE_URL } from 'utils/config'
import ExamAlert from '../gsat2024-2/components/alert/ExamAlert'
import useGetIsPreReserved from '../hooks/useGetIsPreReserved'

export interface IFExamContextProviderProps {
  children: ReactNode
  companyId: string
  termId: string
}

type ThandleCheckReservationOptions = {
  onReservated?: () => void
  onNotReservated?: () => void
}

type ThandleCheckReservationFn = (
  options: ThandleCheckReservationOptions,
) => void | Promise<void>

interface IFExamContextProps {
  isAdmin: boolean
  isLogin: boolean
  isPreReserved: boolean
  isReservationCompleted: boolean
  handleCheckReservation: ThandleCheckReservationFn
}

const ExamContext = createContext<IFExamContextProps>({
  isAdmin: false,
  isLogin: false,
  isPreReserved: false,
  isReservationCompleted: false,
  handleCheckReservation: () => {},
})

const ExamContextProvider: FC<IFExamContextProviderProps> = ({
  children,
  companyId,
  termId,
}) => {
  const router = useRouter()
  const { user } = useCurrentUserContext()
  const { isMobile } = useUA()
  const isPreReserved = useGetIsPreReserved({
    companyId,
    termId,
  })
  const [isOpenAlert, setIsOpenAlert] = useState<boolean>(false)
  const isReservationCompleted = !!router.query.testId

  const isAdmin = !!user?.isSuperuser
  const isLogin = !!user

  const confirmLoginAlert = () => {
    setIsOpenAlert(() => true)
  }

  const handleClickCancel = () => {
    setIsOpenAlert(() => false)
  }

  const handleClickConfirm = () => {
    if (isLogin) {
      router.replace(
        {
          query: {
            ...router.query,
            tab: 'full-reservation',
          },
          hash: '#section1',
        },
        undefined,
        { shallow: true },
      )
      setIsOpenAlert(() => false)
    } else {
      router.push({
        pathname: '/login',
        query: {
          next: `${LINKAREER_BASE_URL}${router.pathname}#section1`,
        },
      })
    }
  }

  const handleCheckReservation: ThandleCheckReservationFn = ({
    onReservated,
    onNotReservated,
  }) => {
    if (isReservationCompleted) {
      if (onReservated) {
        return onReservated()
      }
    } else {
      confirmLoginAlert()
      if (onNotReservated) {
        return onNotReservated()
      }
    }
  }

  const initialValue: IFExamContextProps = {
    isAdmin,
    isLogin,
    isPreReserved,
    isReservationCompleted,
    handleCheckReservation,
  }

  return (
    <ExamContext.Provider value={initialValue}>
      {children}
      {isOpenAlert && (
        <ExamAlert
          type="confirm"
          message={
            isLogin
              ? '서비스 이용 신청이 완료되지 않았습니다.\n서비스 이용을 위해 신청을 먼저 진행해주세요.'
              : '로그인이 필요한 서비스입니다.\n로그인 후 이용해주세요.'
          }
          isMobile={isMobile}
          onClickConfirm={handleClickConfirm}
          onClickCloseModal={handleClickCancel}
        />
      )}
    </ExamContext.Provider>
  )
}

export { ExamContext, ExamContextProvider }
